<script setup>
// The conversations with an SLA (R4), on the kit's table instead of a 12-column grid: conversation (#id with the
// contact and its labels), policy, agent and the SLA events of the row in a kit popover. 25 per page with the kit's
// footer (same `pageChange`); loading and empty as the kit's skeleton and compact Empty (7-7).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'dashboard/components-next/polaris/ui/table';
import { Card } from 'dashboard/components-next/polaris/ui/card';
import { Skeleton } from 'dashboard/components-next/polaris/ui/skeleton';
import {
  Empty,
  EmptyHeader,
  EmptyTitle,
} from 'dashboard/components-next/polaris/ui/empty';
import PaginationFooter from 'dashboard/components-next/polaris/cards/PaginationFooter.vue';
import SLAReportItem from './SLAReportItem.vue';

const props = defineProps({
  slaReports: {
    type: Array,
    default: () => [],
  },
  totalCount: {
    type: Number,
    default: 0,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  pageSize: {
    type: Number,
    default: 25,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['pageChange']);
const { t } = useI18n();
const hasRows = computed(() => props.slaReports.length > 0);
</script>

<template>
  <Card class="min-w-0 gap-0 overflow-hidden py-0" data-sla-table>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead class="px-4">
            {{ t('SLA_REPORTS.TABLE.HEADER.CONVERSATION') }}
          </TableHead>
          <TableHead class="w-56 px-4">
            {{ t('SLA_REPORTS.TABLE.HEADER.POLICY') }}
          </TableHead>
          <TableHead class="w-56 px-4">
            {{ t('SLA_REPORTS.TABLE.HEADER.AGENT') }}
          </TableHead>
          <TableHead class="w-36 px-4">
            <span class="sr-only">{{
              t('SLA_REPORTS.TABLE.VIEW_DETAILS')
            }}</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <template v-if="isLoading">
          <TableRow v-for="n in 5" :key="n">
            <TableCell class="px-4"><Skeleton class="h-5 w-64" /></TableCell>
            <TableCell class="px-4"><Skeleton class="h-5 w-32" /></TableCell>
            <TableCell class="px-4"><Skeleton class="h-6 w-32" /></TableCell>
            <TableCell class="px-4">
              <Skeleton class="ms-auto h-5 w-20" />
            </TableCell>
          </TableRow>
        </template>
        <TableRow v-else-if="!hasRows" class="hover:bg-transparent">
          <TableCell :colspan="4" class="p-0">
            <Empty class="border-0 md:p-8">
              <EmptyHeader>
                <EmptyTitle>{{ t('SLA_REPORTS.NO_RECORDS') }}</EmptyTitle>
              </EmptyHeader>
            </Empty>
          </TableCell>
        </TableRow>
        <template v-else>
          <SLAReportItem
            v-for="slaReport in slaReports"
            :key="slaReport.applied_sla.id"
            :sla-name="slaReport.applied_sla.sla_name"
            :conversation="slaReport.conversation"
            :conversation-id="slaReport.conversation.id"
            :sla-events="slaReport.sla_events"
          />
        </template>
      </TableBody>
    </Table>
    <div
      v-if="totalCount && !isLoading"
      class="border-0 border-t border-solid border-polaris-border px-4 py-3"
    >
      <PaginationFooter
        :page="currentPage"
        :total="totalCount"
        :page-size="pageSize"
        count-key="POLARIS_REPORTS.SLA.COUNT"
        showing-key="POLARIS_REPORTS.SLA.SHOWING"
        @update:page="value => emit('pageChange', value)"
      />
    </div>
  </Card>
</template>
